import { useEffect, useLayoutEffect, useRef } from 'react';
import { PanelContainer } from './PanelContainer';
import { usePanelNavigation } from './PanelNavigationContext';
import { panelBackdrop } from './panelSystem.styles';
import { useScrollLock } from '@/src/lib/hooks/ui/useScrollLock';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const PAGE_SCROLL_KEY = '__page__';

/**
 * Renders all panels in the stack as stacked scrollers.
 * Each panel below the top is hidden with visibility:hidden (desktop) or display:none (mobile).
 *
 * Desktop: useScrollLock prevents background scroll. Panels use position:absolute inside sidebar.
 * Mobile: Panels are in normal document flow (body scrolls → Safari chrome minimizes).
 *         Page content is hidden by PersistentSearchLayout when panels are open.
 *
 * Mobile scroll preservation uses useLayoutEffect to save and restore scroll positions
 * before the browser paints, eliminating visible jank on panel transitions.
 */
export function PanelHost() {
  const { stack, hasPanels, popPanel, clearPanels, scrollCaptureRef } = usePanelNavigation();
  const isDesktop = useIsDesktop();

  // Only lock scroll on desktop where panels overlay the sidebar
  useScrollLock(hasPanels && isDesktop);

  // Per-panel scroll positions. PAGE_SCROLL_KEY = page scroll before any panels opened.
  const scrollMapRef = useRef(new Map<string, number>());
  const prevStackIdsRef = useRef<Array<string>>([]);
  // Track scroll position continuously via passive listener.
  // Reading window.scrollY inside useLayoutEffect after React commits DOM changes
  // (e.g., display:none on children) triggers a forced reflow that clamps the value to 0.
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);

  useEffect(() => {
    if (isDesktop) return;
    const handleScroll = () => {
      lastScrollYRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop]);

  // Save and restore scroll positions before the browser paints.
  // Using useLayoutEffect ensures scrollTo() takes effect before the first frame,
  // eliminating visible jank on panel transitions.
  useIsomorphicLayoutEffect(() => {
    const currIds = stack.map((p) => p.id);
    const prevIds = prevStackIdsRef.current;
    const currLen = currIds.length;
    const prevLen = prevIds.length;

    if (isDesktop) {
      prevStackIdsRef.current = currIds;
      return;
    }

    if (currLen > prevLen) {
      // Push: save outgoing panel's scroll position, scroll to top.
      // Prefer scrollCaptureRef (captured synchronously in pushPanel before DOM changes)
      // over lastScrollYRef (passive listener — usually accurate but can lag).
      const outgoingId = prevLen > 0 ? prevIds[prevLen - 1] : PAGE_SCROLL_KEY;
      const scrollY = scrollCaptureRef.current || lastScrollYRef.current;
      scrollMapRef.current.set(outgoingId, scrollY);
      window.scrollTo(0, 0);
    } else if (currLen < prevLen && currLen > 0) {
      // Pop (still have panels): restore the revealed panel's scroll
      for (let i = currLen; i < prevLen; i++) {
        scrollMapRef.current.delete(prevIds[i]);
      }
      const restoreId = currIds[currLen - 1];
      const savedScroll = scrollMapRef.current.get(restoreId) ?? 0;
      window.scrollTo(0, savedScroll);
    } else if (currLen === 0 && prevLen > 0) {
      // Clear / final pop: restore page scroll
      const pageScroll = scrollMapRef.current.get(PAGE_SCROLL_KEY) ?? 0;
      scrollMapRef.current.clear();
      window.scrollTo(0, pageScroll);
    }

    prevStackIdsRef.current = currIds;
  }, [stack, isDesktop]);

  return (
    <div>
      {hasPanels && <div className={panelBackdrop} />}
      {stack.map((panel, index) => {
        const isTopPanel = index === stack.length - 1;
        const PanelContent = panel.content;

        return (
          <PanelContainer
            key={panel.id}
            isVisible={isTopPanel}
            title={panel.title}
            onBack={panel.urlConfig ? clearPanels : popPanel}
          >
            <PanelContent panelProps={panel.panelProps} />
          </PanelContainer>
        );
      })}
    </div>
  );
}
